/**
 * Article Type Master Management
 * DS-Wiki Knowledge Share Module
 */

import { useState, useEffect, useMemo } from 'react';
import { DataGrid } from '@/lib/dreamstep-sdk/datagrid';
import type { ColumnConfig, GridConfig } from '@/lib/dreamstep-sdk/datagrid';
import dsWikiService from '../../services/dsWikiService';
import type { ArticleType } from '../../services/types';
import ArticleTypeForm from './ArticleTypeForm';
import { useToast } from '../../../../../hooks/useToast';
import ToastContainer from '../../../../../components/feature/ToastContainer';
import ConfirmDialog from '../../../../../components/base/ConfirmDialog';
import AccessDeniedDisplay from '../../../../../components/base/AccessDeniedDisplay';
import { PermissionModal } from '../../../../../components/masters/permission';
import { Breadcrumb } from '../../../../../components/base';
import { TableSkeleton } from '@/components/base/Skeleton';

export default function ArticleTypeManagement() {
  const [articleTypes, setArticleTypes] = useState<(ArticleType & { sno: number })[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingArticleType, setEditingArticleType] = useState<ArticleType | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; articleType: ArticleType | null }>({
    isOpen: false,
    articleType: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const { toasts, removeToast, success, error: showError } = useToast();
  const [permissions, setPermissions] = useState<any>({
    c_in: true, r_in: true, u_in: true, d_in: true, customPermissions: {},
  });

  useEffect(() => { fetchArticleTypes(); }, []);

  const fetchArticleTypes = async () => {
    setLoading(true);
    try {
      const response = await dsWikiService.getAllArticleTypes();
      const data = Array.isArray(response.data) ? response.data : [];
      setArticleTypes(data.map((item, index) => ({ ...item, sno: index + 1 })));
    } catch (error: any) {
      console.error('Error fetching article types:', error);
      showError(error.message || 'Failed to fetch article types');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (articleType?: ArticleType) => { setEditingArticleType(articleType || null); setIsFormOpen(true); };
  const handleCloseForm = () => { setIsFormOpen(false); setEditingArticleType(null); };
  const handleFormSuccess = () => { fetchArticleTypes(); };
  const handleDeleteClick = (articleType: ArticleType) => { setDeleteDialog({ isOpen: true, articleType }); };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.articleType) return;
    setIsDeleting(true);
    try {
      await dsWikiService.deleteArticleType(deleteDialog.articleType.dswiki_artcl_type_id!);
      success('Article type deleted successfully!');
      fetchArticleTypes();
      setDeleteDialog({ isOpen: false, articleType: null });
    } catch (error: any) {
      showError(error?.message || 'Failed to delete article type');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: ColumnConfig[] = useMemo(() => [
    { field: 'sno', columnName: 'SNo', columnType: 'number', width: '80px', visible: true, filter: true, alignment: 'center',
      cellRenderer: (row: any) => <div className="font-mono text-gray-600 font-semibold">{row.sno}</div> },
    { field: 'artcl_type_nm', columnName: 'Article Type Name', columnType: 'text', width: '250px', visible: true, filter: true,
      cellRenderer: (row: any) => <span className="text-gray-900 font-medium">{row.artcl_type_nm}</span> },
    { field: 'artcl_type_cd', columnName: 'Code', columnType: 'text', width: '130px', visible: true, filter: true,
      cellRenderer: (row: any) => <span className="font-mono text-sm text-gray-700">{row.artcl_type_cd}</span> },
    { field: 'dscn_tx', columnName: 'Description', columnType: 'text', width: '200px', visible: true, filter: true },
    { field: 'nd_aprvl_in', columnName: 'Needs Approval', columnType: 'text', width: '120px', visible: true, filter: true, alignment: 'center',
      cellRenderer: (row: any) => row.nd_aprvl_in ? <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Yes</span> : <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">No</span> },
    { field: 'nd_crtn_in', columnName: 'Needs Curation', columnType: 'text', width: '120px', visible: true, filter: true, alignment: 'center',
      cellRenderer: (row: any) => row.nd_crtn_in ? <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Yes</span> : <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">No</span> },
    { field: 'a_in', columnName: 'Active', columnType: 'text', width: '100px', visible: true, filter: true, alignment: 'center',
      cellRenderer: (row: any) => row.a_in ? <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs">Active</span> : <span className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs">Inactive</span> },
    { field: 'crte_usr_nm', columnName: 'Created By', columnType: 'text', width: '150px', visible: false, filter: true },
    { field: 'i_ts', columnName: 'Created On', columnType: 'text', width: '160px', visible: false, filter: true },
    { field: 'actions', columnName: 'Actions', columnType: 'button', width: '150px', visible: Boolean(permissions?.u_in || permissions?.d_in), alignment: 'center', filter: false,
      cellRenderer: (row: any) => (
        <div className="flex items-center justify-center space-x-2" onClick={(e) => e.stopPropagation()}>
          {permissions?.u_in && <button onClick={(e) => { e.stopPropagation(); handleOpenForm(row); }} className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded" title="Edit"><i className="ri-edit-line text-lg"></i></button>}
          {permissions?.d_in && <button onClick={(e) => { e.stopPropagation(); handleDeleteClick(row); }} className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded" title="Delete"><i className="ri-delete-bin-line text-lg"></i></button>}
        </div>
      ) }
  ], [permissions]);

  const gridConfig: GridConfig = useMemo(() => ({
    enableSearchPanel: true, enableColumnChooser: true, pagination: true, minPageSizes: [10, 25, 50], enableColumnResizing: true,
    headerCustomization: { style: 'modern', headerBackgroundColor: '#dbeafe', headerTextColor: '#1e40af', showBorders: true },
    export: { enabled: true, formats: ['excel', 'pdf', 'csv'], title: 'Article Types Master' },
    actionButton: permissions?.c_in ? { label: 'Add Article Type', icon: 'ri-add-line', onClick: () => handleOpenForm(), visible: true } : undefined,
  }), [permissions]);

  return (
    <div className="h-full flex flex-col min-w-0 bg-gray-50">
      {/* Sleek Header */}
      <div className="flex-shrink-0 module-header shadow-sm">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Breadcrumb, Icon & Title */}
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg">
                <i className="ri-file-list-3-line text-xl text-white"></i>
              </div>
              <div>
                <Breadcrumb
                  items={[{ label: 'DS-Wiki' }, { label: 'Masters' }]}
                  currentPage="Article Types"
                />
                <h1 className="text-lg font-semibold text-white">
                  Article Types
                </h1>
              </div>
            </div>
            {/* Right: Stats & Actions */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-white/90 text-sm">
                <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                <span><span className="font-semibold">{articleTypes.length}</span> records</span>
              </div>
              <button
                onClick={() => setIsPermissionModalOpen(true)}
                className="p-1.5 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-colors"
                title="View Permissions"
              >
                <i className="ri-shield-user-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden px-6 pb-6 pt-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm h-full overflow-hidden flex flex-col p-4">
          {loading ? (
            <TableSkeleton />
          ) : (permissions as any)?.accessDenied ? (
            <AccessDeniedDisplay message={(permissions as any).accessDeniedMessage} />
          ) : articleTypes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4"><i className="ri-file-list-3-line text-gray-400 text-4xl"></i></div>
              <p className="text-gray-600 font-medium text-lg">No article types found</p>
              {permissions?.c_in && <button onClick={() => handleOpenForm()} className="mt-6 flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-md"><i className="ri-add-line mr-2"></i>Add First Article Type</button>}
            </div>
          ) : (
            <DataGrid dataSource={articleTypes} columns={columns} gridConfig={gridConfig} viewConfig={{ format: 'table' }} />
          )}
        </div>
      </div>

      <ArticleTypeForm isOpen={isFormOpen} articleType={editingArticleType} onClose={handleCloseForm} onSuccess={handleFormSuccess} />
      <ConfirmDialog isOpen={deleteDialog.isOpen} onClose={() => setDeleteDialog({ isOpen: false, articleType: null })} onConfirm={handleDeleteConfirm} title="Delete Article Type" message={`Are you sure you want to delete "${deleteDialog.articleType?.artcl_type_nm}"?`} confirmText="Delete" cancelText="Cancel" type="danger" isLoading={isDeleting} />
      <PermissionModal isOpen={isPermissionModalOpen} onClose={() => setIsPermissionModalOpen(false)} permissions={permissions} entityName="Article Types Master" />
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
